import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Text,
  HStack,
  useToast,
  useMediaQuery,
  Flex,
  FormControl,
  FormLabel,
  Textarea,
  FormErrorMessage,
} from '@chakra-ui/react';
import { Form, Formik, Field } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import FormField from 'components/FormField';
import { AppDispatch } from 'utils/GlobalHelpers';
import { STATUS } from 'projectConstants';
import {
  getIsPageModalOpen,
  getEditPageData,
  getDashboard,
  getCreatePageStatus,
  getUpdatePageStatus,
  closePageModal,
  createPage,
  updatePage,
} from 'redux/DashboardSliceV2';

const PageModal = () => {
  const dispatch: AppDispatch = useDispatch();
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 575px)');

  const isOpen = useSelector(getIsPageModalOpen);
  const editPageData = useSelector(getEditPageData);
  const dashboard = useSelector(getDashboard);
  const createPageStatus = useSelector(getCreatePageStatus);
  const updatePageStatus = useSelector(getUpdatePageStatus);
  const [isLoading, setIsLoading] = useState(false);

  const isEditing = editPageData && Object.keys(editPageData).length > 0;

  useEffect(() => {
    if (createPageStatus === STATUS.SUCCESS) {
      toast({
        title: 'Success',
        description: 'Page created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      dispatch(closePageModal());
    } else if (createPageStatus === STATUS.FAILED) {
      toast({
        title: 'Error',
        description: 'Failed to create page',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [createPageStatus, toast, dispatch]);

  useEffect(() => {
    if (updatePageStatus === STATUS.SUCCESS) {
      toast({
        title: 'Success',
        description: 'Page updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      dispatch(closePageModal());
    } else if (updatePageStatus === STATUS.FAILED) {
      toast({
        title: 'Error',
        description: 'Failed to update page',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [updatePageStatus, toast, dispatch]);

  const handleClose = () => {
    dispatch(closePageModal());
  };

  const handleSubmit = async (form: any) => {
    setIsLoading(true);
    if (!dashboard?.id) {
      toast({
        title: 'Error',
        description: 'Dashboard ID is missing',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (isEditing) {
      const requestBody = {
        id: editPageData.id,
        name: form.name.trim(),
        description: form.description.trim(),
        position: editPageData?.position || 0,
        dashboard_id: editPageData.dashboard_id,
        created_at: editPageData.created_at,
        updated_at: editPageData.updated_at,
        instrument_layout: editPageData?.instrument_layout || {},
      };
      dispatch(
        updatePage({
          request_body: requestBody,
        }),
      ).then(() => {
        setIsLoading(false);
      });
    } else {
      const requestBody = {
        name: form.name.trim(),
        description: form.description.trim(),
        dashboard_id: dashboard.id,
        position: (dashboard.pages?.length || 0) + 1,
      };
      dispatch(
        createPage({
          request_body: requestBody,
        }),
      ).then(() => {
        setIsLoading(false);
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='md' isCentered blockScrollOnMount={false}>
      <ModalOverlay />
      <ModalContent p={3} maxW='50rem'>
        <Formik
          initialValues={{
            name: isEditing ? editPageData?.name || '' : '',
            description: isEditing ? editPageData?.description || '' : '',
          }}
          onSubmit={(form) => handleSubmit(form)}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting }: any) => {
            return (
              <Form>
                <ModalHeader position='relative' px={6} pt={3} pb={0}>
                  <Text variant='modals'>{isEditing ? 'Edit Page' : 'Create new page'}</Text>
                </ModalHeader>
                <ModalBody>
                  <Flex gap={4} color='black' flexWrap={isMobile ? 'wrap' : 'initial'}>
                    <Flex alignItems={'flex-start'} gap='5' width={'100%'} flexDir={'column'}>
                      <FormField
                        label='Page name *'
                        name='name'
                        type='text'
                        labelStyles={{
                          fontSize: '15px',
                          color: '#212121',
                        }}
                        inputStyles={{
                          boxShadow: '0px 0px 0px 1.5px #EEF1F6',
                          height: '35px',
                          paddingInlineStart: '8px',
                          fontSize: '15px',
                        }}
                        touched={touched.name}
                        error={errors.name}
                      />
                      <FormControl>
                        <FormLabel fontSize='15px' color='#212121' fontFamily='regular_roboto'>
                          Page description
                        </FormLabel>
                        <Field
                          as={Textarea}
                          name='description'
                          labelStyles={{
                            fontSize: '15px',
                            color: '#212121',
                          }}
                          placeholder='Enter page description'
                          resize='none'
                          boxShadow='0px 0px 0px 1.5px #EEF1F6'
                          width='100%'
                          padding='8px 15px 8px 8px'
                          variant='filled'
                          autoComplete='off'
                          minH='70px'
                          borderRadius='8px'
                          fontFamily='regular_roboto'
                          fontSize='15px'
                          color='black.900'
                          bgColor='white.900'
                          _focusVisible={{
                            boxShadow: '',
                            bgColor: 'white.900',
                          }}
                          inputStyles={{
                            boxShadow: '0px 0px 0px 1.5px #EEF1F6',
                            height: '35px',
                            paddingInlineStart: '8px',
                            fontSize: '15px',
                          }}
                          _placeholder={{ color: 'grey.600' }}
                        />
                        <FormErrorMessage
                          fontFamily='regular_roboto'
                          fontSize='3xs'
                          color='red.500'
                        >
                          {errors.description}
                        </FormErrorMessage>
                      </FormControl>
                    </Flex>
                  </Flex>
                </ModalBody>
                <ModalFooter pb='0'>
                  <HStack spacing='5'>
                    <Button
                      px={'10'}
                      w='fit-content'
                      h={'10'}
                      borderRadius='10px'
                      fontSize='xs'
                      color='violet.800'
                      bgColor='grey.500'
                      onClick={handleClose}
                      isDisabled={isSubmitting || isLoading}
                    >
                      CANCEL
                    </Button>
                    <Button
                      px={'10'}
                      _hover={{ _disabled: {} }}
                      type='submit'
                      w='fit-content'
                      h={'10'}
                      borderRadius='10px'
                      fontSize='xs'
                      bgColor='violet.800'
                      isLoading={isSubmitting}
                      loadingText='SAVING...'
                      disabled={isSubmitting || isLoading}
                    >
                      SAVE
                    </Button>
                  </HStack>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default PageModal;
